import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { Alert, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { Subscription, BillingHistory, User } from '../../types/dashboard';
import CustomButton from '../ui/Button';
import { apiService, formatApiErrorDetail, ApiError } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

function billingErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    return formatApiErrorDetail(e.response) || e.message || fallback;
  }
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message || fallback;
  }
  return fallback;
}

const PURCHASABLE_PLANS = new Set(['starter', 'pro', 'elite', 'ultimate']);

export interface SubscriptionManagementProps {
  subscription: Subscription;
  billingHistory: BillingHistory[];
  user: User;
  onPlanChange: (newPlan: Subscription['plan']) => void;
  onPaymentMethodUpdate: () => void;
  onUserInfoUpdate: (userInfo: Partial<User>) => void | Promise<void>;
  loading?: boolean;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({
  subscription,
  user,
  onUserInfoUpdate,
  loading = false,
}) => {
  const { token: authToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPlanChangeDialog, setShowPlanChangeDialog] = useState(false);
  const [showUserEditDialog, setShowUserEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(user);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [taxUse, setTaxUse] = useState<'personal' | 'business' | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const autoCheckoutStarted = useRef(false);

  // Plans mirror backend SubscriptionService / marketing site pricing.
  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 30,
      priceYearly: 26,
      webhookLimit: 3,
      signalLimit: 2,
      alertLimit: 20,
      features: [
        '20 alert setups',
        '3 outbound webhooks per alert',
        '2 signals per route',
        'Unlimited signal platforms',
      ],
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 40,
      priceYearly: 34,
      webhookLimit: 6,
      signalLimit: 3,
      alertLimit: 40,
      features: [
        '40 alert setups',
        '6 outbound webhooks per alert',
        '3 signals per route',
        'Unlimited signal platforms',
      ],
    },
    {
      id: 'elite',
      name: 'Elite',
      price: 60,
      priceYearly: 51,
      webhookLimit: 9,
      signalLimit: 5,
      alertLimit: 60,
      features: [
        '60 alert setups',
        '9 outbound webhooks per alert',
        '5 signals per route',
        'Unlimited signal platforms',
      ],
    },
    {
      id: 'ultimate',
      name: 'Ultimate',
      price: 80,
      priceYearly: 68,
      webhookLimit: 999,
      signalLimit: 10,
      alertLimit: null as number | null,
      features: [
        'Unlimited alert setups',
        'Unlimited outbound webhooks per alert',
        '10 signals per route',
        'Unlimited signal platforms',
      ],
    },
  ];

  const normalizePlanId = (planId: string) => {
    const legacy: Record<string, string> = {
      basic: 'starter',
      standard: 'pro',
      enterprise: 'ultimate',
      desktop: 'pro',
    };
    return legacy[planId] || planId;
  };

  const currentPlan = plans.find(
    (plan) => normalizePlanId(plan.id) === normalizePlanId(subscription.plan)
  );
  // Tiers like trial/desktop/admin aren't in the purchasable `plans` list; show a sensible label.
  const planDisplayName =
    currentPlan?.name ??
    (subscription.plan
      ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)
      : 'Your plan');

  // Start Stripe Checkout for the selected tier and redirect to the hosted page.
  const handleCheckout = async (
    planId: string,
    cycle: 'monthly' | 'yearly' = billingCycle,
    use: 'personal' | 'business' | null = taxUse,
  ) => {
    if (!use) {
      setActionError('Select personal or business use first. This chooses the correct sales-tax category.');
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const res = await apiService.createCheckoutSession(
        { subscription_tier: planId, billing_cycle: cycle, tax_use: use },
        authToken || undefined,
      );
      const url = res.success ? res.data?.checkout_url : undefined;
      if (url) {
        window.location.assign(url);
        return;
      }
      setActionError(res.error || 'Could not start checkout. Please try again.');
    } catch (e: any) {
      setActionError(billingErrorMessage(e, 'Checkout is unavailable right now.'));
    } finally {
      setBusy(false);
    }
  };

  // Marketing / signup deep-link: /settings?checkout=1&plan=pro&billing=monthly&use=business
  useEffect(() => {
    if (autoCheckoutStarted.current || loading || !authToken) return;
    if (searchParams.get('checkout') !== '1') return;

    const plan = (searchParams.get('plan') || '').toLowerCase();
    const rawBilling = (searchParams.get('billing') || 'monthly').toLowerCase();
    const cycle: 'monthly' | 'yearly' =
      rawBilling === 'yearly' || rawBilling === 'annual' || rawBilling === 'year'
        ? 'yearly'
        : 'monthly';
    const rawUse = (searchParams.get('use') || searchParams.get('tax_use') || '').toLowerCase();
    const use: 'personal' | 'business' | null =
      rawUse === 'business' || rawUse === 'personal' ? rawUse : null;

    autoCheckoutStarted.current = true;
    setBillingCycle(cycle);
    if (use) setTaxUse(use);

    // Clear query so refresh does not re-trigger checkout.
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    next.delete('plan');
    next.delete('billing');
    next.delete('use');
    next.delete('tax_use');
    setSearchParams(next, { replace: true });

    if (PURCHASABLE_PLANS.has(plan) && use) {
      void handleCheckout(plan, cycle, use);
    } else {
      setShowPlanChangeDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once from deep-link params
  }, [authToken, loading, searchParams, setSearchParams]);

  // Open the Stripe Customer Portal for self-service billing management.
  const handleManageBilling = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const res = await apiService.createBillingPortalSession(
        `${window.location.origin}/settings`,
        authToken || undefined,
      );
      const url = res.success ? res.data?.portal_url : undefined;
      if (url) {
        window.location.assign(url);
        return;
      }
      setActionError(res.error || 'Could not open the billing portal. Please try again.');
    } catch (e: any) {
      setActionError(billingErrorMessage(e, 'Billing management is unavailable right now.'));
    } finally {
      setBusy(false);
    }
  };

  const handleUserInfoSave = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await onUserInfoUpdate({ name: editingUser.name, email: editingUser.email });
      setShowUserEditDialog(false);
    } catch (e: any) {
      setActionError(e?.message || 'Could not save your changes. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getUsageColor = (usage: number, limit: number) => {
    const percentage = (usage / limit) * 100;
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  return (
    <Box>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {/* Current Plan Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Current Plan"
          action={
            <Box display="flex" gap={1}>
              <CustomButton
                variant="secondary"
                onClick={handleManageBilling}
                disabled={loading || busy}
              >
                Manage Billing
              </CustomButton>
              <CustomButton
                variant="primary"
                onClick={() => setShowPlanChangeDialog(true)}
                disabled={loading || busy}
              >
                Change Plan
              </CustomButton>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h5">{planDisplayName}</Typography>
                <Chip
                  label={subscription.status}
                  color={subscription.status === 'active' ? 'success' : 'warning'}
                  size="small"
                />
              </Box>
              {currentPlan && (
                <Typography variant="h4" color="primary" mb={2}>
                  {formatCurrency(currentPlan.price)}
                  <Typography component="span" variant="body1" color="text.secondary">
                    /month
                  </Typography>
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" mb={2}>
                Expires: {formatDate(subscription.expiresAt)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">Webhook Usage</Typography>
                  <Typography variant="body2">
                    {(subscription.webhookLimit || 0) >= 999
                      ? 'Unlimited'
                      : `${subscription.webhookUsage || 0} / ${subscription.webhookLimit || 1}`}
                  </Typography>
                </Box>
                {(subscription.webhookLimit || 0) < 999 && (
                  <LinearProgress
                    variant="determinate"
                    value={subscription.webhookLimit ? ((subscription.webhookUsage || 0) / subscription.webhookLimit) * 100 : 0}
                    color={getUsageColor(subscription.webhookUsage || 0, subscription.webhookLimit || 1)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payment Method Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Payment Method"
          action={
            <CustomButton
              variant="secondary"
              onClick={handleManageBilling}
              disabled={loading || busy}
            >
              Manage Billing
            </CustomButton>
          }
        />
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <CreditCardIcon />
            <Typography variant="body2" color="text.secondary">
              Payment methods are managed securely in the Stripe Customer Portal. Click
              “Manage Billing” to add or update a card — your card details never touch our servers.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* User Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Personal Information"
          action={
            <CustomButton
              variant="secondary"
              onClick={() => setShowUserEditDialog(true)}
              disabled={loading}
            >
              Edit
            </CustomButton>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Name</Typography>
              <Typography variant="body1">{user.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{user.email}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Role</Typography>
              <Typography variant="body1">{user.role}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Member Since</Typography>
              <Typography variant="body1">{formatDate(user.createdAt)}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invoices & billing history are managed in the Stripe Customer Portal (see Payment Method → Manage Billing). */}

      {/* Plan Change Dialog */}
      <Dialog open={showPlanChangeDialog} onClose={() => setShowPlanChangeDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Choose a plan</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" mb={1}>
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              size="small"
              onChange={(_e, value) => value && setBillingCycle(value)}
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="yearly">Yearly</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box display="flex" justifyContent="center" mb={2}>
            <ToggleButtonGroup
              value={taxUse}
              exclusive
              size="small"
              onChange={(_e, value) => value && setTaxUse(value)}
            >
              <ToggleButton value="personal">Personal use</ToggleButton>
              <ToggleButton value="business">Business use</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            Plan prices are the same. Personal vs business selects the Stripe Tax category
            (required for sales tax). Business checkout can collect a VAT/GST ID.
          </Typography>
          <Grid container spacing={2}>
            {plans.map((plan) => {
              const isCurrent =
                normalizePlanId(plan.id) === normalizePlanId(subscription.plan);
              const displayPrice =
                billingCycle === 'yearly'
                  ? (plan.priceYearly ?? plan.price)
                  : plan.price;
              return (
                <Grid item xs={12} sm={6} key={plan.id}>
                  <Card
                    variant={isCurrent ? 'outlined' : 'elevation'}
                    sx={{
                      border: isCurrent ? 2 : 1,
                      borderColor: isCurrent ? 'primary.main' : 'divider',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="h6">{plan.name}</Typography>
                        {isCurrent && <Chip label="Current" color="primary" size="small" />}
                      </Box>
                      <Typography variant="h4" color="primary" gutterBottom>
                        {formatCurrency(displayPrice)}
                        <Typography component="span" variant="body1" color="text.secondary">
                          {billingCycle === 'yearly' ? '/mo, billed yearly' : '/month'}
                        </Typography>
                      </Typography>
                      <Box>
                        {plan.features.map((feature, index) => (
                          <Typography key={index} variant="body2" color="text.secondary">
                            • {feature}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                    <DialogActions sx={{ px: 2, pb: 2 }}>
                      <CustomButton
                        variant="primary"
                        onClick={() => handleCheckout(plan.id)}
                        disabled={busy || isCurrent || !taxUse}
                      >
                        {isCurrent ? 'Current plan' : busy ? 'Redirecting…' : 'Select'}
                      </CustomButton>
                    </DialogActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {subscription.plan === 'trial'
              ? 'You’ll add a card on Stripe’s secure checkout and start a 7-day trial. You are not charged until the trial ends.'
              : 'You’ll be redirected to Stripe’s secure checkout to complete your subscription.'}
            {' '}
            Listed prices do not include tax. Sales tax is location-specific and is charged
            when we have tax nexus in your billing location. If tax is not charged today
            because we do not have nexus there, later renewals and new purchases may include
            sales tax if nexus is established later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setShowPlanChangeDialog(false)}>
            Cancel
          </CustomButton>
        </DialogActions>
      </Dialog>

      {/* User Edit Dialog */}
      <Dialog open={showUserEditDialog} onClose={() => setShowUserEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Personal Information</DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Name
              </Typography>
              <input
                type="text"
                value={editingUser.name}
                onChange={(e) => setEditingUser(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '16px',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email
              </Typography>
              <input
                type="email"
                value={editingUser.email}
                onChange={(e) => setEditingUser(prev => ({ ...prev, email: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '16px',
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setShowUserEditDialog(false)} disabled={busy}>
            Cancel
          </CustomButton>
          <CustomButton variant="primary" onClick={handleUserInfoSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save Changes'}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionManagement;
